import Utf8 from 'crypto-js/enc-utf8';
import Base64 from 'crypto-js/enc-base64';

// Sales captured while state.user was empty carry no user_id and ids built as `${undefined}-${timestamp}`
const MISSING_USER_PREFIX = 'undefined-';

// The server rejects a sale signed without the user's id or result_data_key, on every retry
const canSignResults = user => !!(user?.id && user?.result_data_key);

const decodeResultData = encodedData => {
  try {
    const payload = encodedData.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(Utf8.stringify(Base64.parse(payload + '==='.slice((payload.length + 3) % 4))));
  } catch (error) {
    return null;
  }
};

const replaceMissingUserId = (id, userId) =>
  typeof id === 'string' && id.startsWith(MISSING_USER_PREFIX)
    ? `${userId}-${id.slice(MISSING_USER_PREFIX.length)}`
    : id;

// Gives a sale captured without a user the current user's id, in user_id and in the ids built from it
const withResultUser = (data, userId) => {
  const ids = ['id', 'offline_id', 'session_id'].reduce((acc, key) => {
    if (data[key] !== undefined) acc[key] = replaceMissingUserId(data[key], userId);
    return acc;
  }, {});

  return { ...data, ...ids, user_id: data.user_id || userId };
};

const isOrphanedOfflineResult = offlineResult => {
  const { offlineData, unsignedData } = offlineResult || {};
  if (unsignedData) return !unsignedData.user_id;

  const payload = typeof offlineData === 'string' ? decodeResultData(offlineData) : null;
  return !!payload && !payload.user_id && String(payload.session_id).startsWith(MISSING_USER_PREFIX);
};

const recoverOfflineResult = (offlineResult, userId, sign) => {
  const { id, offline_id, offlineData, unsignedData, uploadedFiles } = offlineResult;
  const recovered = {
    ...offlineResult,
    id: replaceMissingUserId(id, userId),
    offline_id: replaceMissingUserId(offline_id, userId),
    user_id: userId
  };

  if (uploadedFiles) {
    recovered.uploadedFiles = Object.entries(uploadedFiles).reduce((acc, [key, file]) => {
      acc[key] = { ...file, offline_id: replaceMissingUserId(file?.offline_id, userId) };
      return acc;
    }, {});
  }

  // unsigned sales are signed when they are sent
  if (unsignedData) recovered.unsignedData = withResultUser(unsignedData, userId);
  else recovered.offlineData = sign(withResultUser(decodeResultData(offlineData), userId));

  return recovered;
};

/**
 * Moves the offline sales captured without a user (stored under result["undefined"], unsigned or signed with
 * `${encodeKey}undefined`) to result[userId], with the user's ids and, when they were signed, signed again by `sign`.
 * Returns the same object when there is nothing to recover.
 */
const recoverOfflineResults = (resultState, userId, sign) => {
  const userKey = String(userId);
  const recovered = [];
  const nextState = { ...resultState };
  const movedPictures = [];

  for (const [key, userResults] of Object.entries(resultState || {})) {
    const offline = userResults?.offline || [];
    const orphaned = offline.filter(isOrphanedOfflineResult);
    if (!orphaned.length && key !== 'undefined') continue;

    recovered.push(...orphaned.map(offlineResult => recoverOfflineResult(offlineResult, userId, sign)));
    const remaining = offline.filter(offlineResult => !orphaned.includes(offlineResult));
    const emptied = key === 'undefined' && !remaining.length;

    // pictures are stored with their sale's offline id: they move with the recovered sales, and an emptied
    // "undefined" bucket hands over all of its pictures
    const recoveredIds = new Set(orphaned.flatMap(({ id, offline_id }) => [id, offline_id]).filter(Boolean));
    const pictures = userResults?.offlinePictures || [];
    const moving = pictures.filter(picture => emptied || recoveredIds.has(picture?.offline_id));
    movedPictures.push(
      ...moving.map(picture =>
        picture.offline_id ? { ...picture, offline_id: replaceMissingUserId(picture.offline_id, userId) } : picture
      )
    );

    if (emptied) {
      delete nextState[key];
    } else {
      nextState[key] = {
        ...userResults,
        offline: remaining,
        offlinePictures: pictures.filter(picture => !moving.includes(picture))
      };
    }
  }

  if (!recovered.length && !(resultState && 'undefined' in resultState)) return resultState;

  const current = nextState[userKey] || {};
  nextState[userKey] = {
    results: [],
    ...current,
    offline: [...(current.offline || []), ...recovered],
    offlinePictures: [...(current.offlinePictures || []), ...movedPictures]
  };

  return nextState;
};

export { canSignResults, recoverOfflineResults, withResultUser };
